<template>
  <div>
    <DxForm ref="formInstance" :form-data="model" label-location="top" :show-colon-after-label="false"
      :visible="model != null" validation-group="validacionClienteInmobiliario">
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <DxSimpleItem :col-span="6" :visible="!props.idRelacionClienteInmobiliario">
        <span class="dx-field-item-label-text text-success">
          {{ $t(props.tipoRelacion === TipoRelacionClienteInmobiliarioEnum.Inquilino ? 
            'clienteInmobiliario.ui.tipoClienteInmueble' : 
            'clienteInmobiliario.ui.tipoPropietario') }}
        </span>
      </DxSimpleItem>

      <DxSimpleItem :col-span="6" :visible="!props.idRelacionClienteInmobiliario">
        <DxRadioGroup
          v-model="tipoClienteInmueble"
          :items="tiposClienteInmueble"
          value-expr="value"
          display-expr="text"
          layout="horizontal"
          @value-changed="onTipoClienteInmuebleChanged"
        />
      </DxSimpleItem>

      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem :visible="tipoClienteInmueble === 'existente'" :col-span="4">
          <DxLabel :text="$t(props.tipoRelacion === TipoRelacionClienteInmobiliarioEnum.Inquilino ? 
            'clienteInmobiliario.ui.inquilino' : 
            'clienteInmobiliario.ui.propietario')" />
          <DxDropDownBox
            v-model:value="selectedClienteId"
            :data-source="clientesDataSource"
            display-expr="NombreCompleto"
            :value-expr="'Id'"
            :show-clear-button="true"
            :open-on-field-click="true"
            :drop-down-options="{ width: 800, height: 400 }"
            v-model:opened="gridBoxOpened"
          >
            <template #content>
              <ClienteInmobiliarioGridSelector 
                v-model="selectedClienteId"
                @cliente-selected="onClienteSelected"
              />
            </template>
          </DxDropDownBox>
        </DxSimpleItem>

        <DxEmptyItem :visible="tipoClienteInmueble === 'existente'" :col-span="2"></DxEmptyItem>
      </DxGroupItem>

      <DxSimpleItem :col-span="6">
        <span class="dx-field-item-label-text text-success">{{ $t('clienteInmobiliario.ui.datosRelacion') }}</span>
      </DxSimpleItem>

      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="FechaInicio" editor-type="dxDateBox" :col-span="3" >
          <DxLabel :text="$t('clienteInmobiliario.fechaInicio')" />
          <DxDateBox v-model="model.FechaInicio" validation-message-mode="always" :show-clear-button="true">
            <DxValidator ref="validatorFechaInicio" validation-group="validacionFechaInicio">
              <DxCustomRule
                :message="$t('clienteInmobiliario.validations.fechaInicioInferior')"
                :validation-callback="validateFechaInicio"
                :reevaluate="true"
              />
            </DxValidator>
          </DxDateBox>
        </DxSimpleItem>

        <DxSimpleItem data-field="FechaFin" editor-type="dxDateBox" :col-span="3">
          <DxLabel :text="$t('clienteInmobiliario.fechaFin')" />
          <DxDateBox v-model="model.FechaFin" validation-message-mode="always" :show-clear-button="true">
            <DxValidator ref="validatorFechaFin" validation-group="validacionFechaFin">
              <DxCustomRule
                :message="$t('clienteInmobiliario.validations.fechaFinSuperior')"
                :validation-callback="validateFechaFin"
                :reevaluate="true"
              />
            </DxValidator>
          </DxDateBox>
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
    
    <ClienteInmobiliarioForm 
      ref="formCliente" 
      :is-embedded="true" 
      @cliente-saved="onClienteSaved" 
      :id-cliente-inmobiliario="idClienteInmobiliario"
      :key="idClienteInmobiliario"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted,  getCurrentInstance, watch } from 'vue';
import { DxForm, DxLabel, DxColCountByScreen, DxCustomRule, DxEmptyItem, DxSimpleItem, DxGroupItem } from "devextreme-vue/form";
import { DxDateBox } from "devextreme-vue";
import { useApi } from '@api/integration';
import { TipoRelacionClienteInmobiliarioEnum } from '@api/api';
import validationEngine from "devextreme/ui/validation_engine";
import { useIntl } from '@services/i18n';
import { AddRelacionClienteInmobiliarioInmueble } from './models/AddRelacionClienteInmobiliario';
import { EditRelacionClienteInmobiliarioInmueble } from './models/EditRelacionClienteInmobiliario';
import ClienteInmobiliarioForm from '@/components/clientes-inmobiliarios/DatosClienteInmobiliarioForm.vue';
import { messageBoxResult } from '@components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import DxValidator from 'devextreme-vue/validator';
import { DxRadioGroup, DxDropDownBox } from 'devextreme-vue';
import ClienteInmobiliarioGridSelector from '@/components/clientes-inmobiliarios/ClienteInmobiliarioGridSelector.vue';

export interface Props {
  tipoRelacion: TipoRelacionClienteInmobiliarioEnum;
  idInmueble: string;
  idClienteInmobiliario?: string;
  idRelacionClienteInmobiliario?: string;
}

const props = defineProps<Props>();
const model = ref<AddRelacionClienteInmobiliarioInmueble | EditRelacionClienteInmobiliarioInmueble>(new AddRelacionClienteInmobiliarioInmueble({TipoRelacion: props.tipoRelacion}));
const api = useApi();
const intl = useIntl();
const formCliente = ref<InstanceType<typeof ClienteInmobiliarioForm>>();
const validatorFechaInicio = ref<DxValidator>();
const validatorFechaFin = ref<DxValidator>();

const formInstance = ref<DxForm>();

const tipoClienteInmueble = ref<string>('nuevo');
const selectedClienteId = ref<string>('');
const clientesDataSource = ref<any[]>([]);
const idClienteInmobiliario = ref<string | undefined>(props.idClienteInmobiliario);
const tiposClienteInmueble = [
  { text: intl.i18n.t('clienteInmobiliario.ui.inquilinoNuevo'), value: 'nuevo' },
  { text: intl.i18n.t('clienteInmobiliario.ui.inquilinoExistente'), value: 'existente' }
];
const gridBoxOpened = ref(false);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)
  configureButtons(modal);
  if (props.idRelacionClienteInmobiliario) {
    const detailRelacionClienteInmobiliario = await api.callStandardApi("getClienteInmuebleById", { showLoading: true }, props.idInmueble, props.idRelacionClienteInmobiliario);
    model.value = new EditRelacionClienteInmobiliarioInmueble().createFromDetailRelacionClienteInmobiliario(detailRelacionClienteInmobiliario);
  }
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  });
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const isValid = () => {
  return validationEngine.validateGroup("validacionClienteInmobiliario").isValid && 
         formCliente.value?.isValid();
};

const onClienteSaved = (idCliente: string) => {
  if (!props.idRelacionClienteInmobiliario) {
    (model.value as AddRelacionClienteInmobiliarioInmueble).IdClienteInmobiliario = idCliente;
  }
};

const saveOrEdit = async () => {
  if (isValid()) {
    try {
      // Primero guardamos el cliente
      const clienteGuardado = await formCliente.value!.saveOrEdit();
      
      if (!clienteGuardado) {
        return false;
      }
      
      if (props.idRelacionClienteInmobiliario) {
        const editModel = new EditRelacionClienteInmobiliarioInmueble(model.value);
        editModel.Id = props.idRelacionClienteInmobiliario;
        await api.callStandardApi("editRelacionClienteInmobiliarioInmueble", { showLoading: true }, props.idInmueble, editModel);
      } else {
        await api.callStandardApi("addRelacionClienteInmobiliarioInmueble", { showLoading: true }, props.idInmueble, model.value as AddRelacionClienteInmobiliarioInmueble);
      }

      return true;
    } catch (error) {
      return false;
    }
  }
  return false;
};

const validateFechaInicio = (e: { value: Date | null }) => {
  if (!e.value || !model.value.FechaFin) return true;
  const fechaInicio = new Date(e.value).setHours(0, 0, 0, 0);
  const fechaFin = new Date(model.value.FechaFin).setHours(0, 0, 0, 0);
  return fechaInicio <= fechaFin;
};

const validateFechaFin = (e: { value: Date | null }) => {
  if (!e.value || !model.value.FechaInicio) return true;
  const fechaFin = new Date(e.value).setHours(0, 0, 0, 0);
  const fechaInicio = new Date(model.value.FechaInicio).setHours(0, 0, 0, 0);
  return fechaFin >= fechaInicio;
};

watch(
  () => ({
    fechaInicio: model.value.FechaInicio,
    fechaFin: model.value.FechaFin
  }),
  () => {
    validationEngine.validateGroup("validacionFechaInicio");
    validationEngine.validateGroup("validacionFechaFin");
  }
);

const onTipoClienteInmuebleChanged = () => {
  if (tipoClienteInmueble.value === 'existente') {
    selectedClienteId.value = '';
    clientesDataSource.value = [];
  }
  else {
    idClienteInmobiliario.value = undefined;
  }
};

const onClienteSelected = (cliente: any) => {
  selectedClienteId.value = cliente.Id;
  clientesDataSource.value = [cliente];
  gridBoxOpened.value = false;
  idClienteInmobiliario.value = cliente.Id;
};

defineExpose({
  isValid,
  saveOrEdit
});
</script>
